import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import ChartTooltip from '../chart-tooltip/chart-tooltip.js';
import DownloadAsPNGButton from '../download-png-btn/download-as-png-btn.js';
import DownloadAsExcelButton from '../download-as-excel-btn/download-as-excel-btn.js';
import { formatFilters } from '../../utils/format-filters.js';
import { observeElementSize } from '../../utils/observe-element-size.js';

import './multy-bar-chart.css';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const DEFAULT_SERIES_COLORS = ['#5FD1A0', 'rgba(255,255,255,.2)', '#F27878'];

const BarChart = (props) => {
  const {
    id,
    data,
    xTickTotated,
    height,
    color,
    colorScale: customColorScale,
    fileName,
    columns,
    filters,
    title,
  } = props;

  const chartRootRef = useRef(null);
  const [tooltipTop, setTooltipTop] = useState('auto');
  const [tooltipLeft, setTooltipLeft] = useState('auto');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState('');
  const [tooltipValue, setTooltipValue] = useState('');
  const [width, setWidth] = useState(0);

  const margin = useMemo(
    () => ({ top: 12, right: 16, bottom: xTickTotated ? 92 : 28, left: 64 }),
    [xTickTotated]
  );
  const svgHeight = (height || 260) - margin.bottom - margin.top;

  const safeData = Array.isArray(data) ? data : [];
  const safeColumns = Array.isArray(data?.columns) ? data.columns : [];
  const hasSeries = safeColumns.length > 1 && safeData.length > 0;
  const seriesColors = color || DEFAULT_SERIES_COLORS;

  const defaultColorScale = useMemo(
    () =>
      d3
        .scaleOrdinal()
        .domain(['Positive', 'Reviews', 'Negative'])
        .range(['#5FD1A0', 'rgba(255,255,255,.2)', '#F27878']),
    []
  );

  const legendColorScale = customColorScale || defaultColorScale;
  // Avoid depending on scale function identity — parent re-renders often pass a new
  // d3 scale instance, which used to tear down the SVG without redrawing bars.
  const legendDomainKey = legendColorScale.domain().join('|');
  const seriesColorsKey = seriesColors.join('|');

  useEffect(() => {
    const host = chartRootRef.current;
    if (!host) {
      return undefined;
    }

    const renderFrame = () => {
      const bounds = host.getBoundingClientRect();
      const measuredWidth = clamp(
        bounds.width - margin.left - margin.right,
        280,
        980
      );
      setWidth((previousWidth) =>
        previousWidth === measuredWidth ? previousWidth : measuredWidth
      );
    };

    renderFrame();

    return observeElementSize(host, renderFrame);
  }, [margin.left, margin.right]);

  useEffect(() => {
    const chartRoot = d3.select(`#chart-${id}`);
    chartRoot.select('svg').remove();
    chartRoot.select('.legend-container').remove();

    if (!hasSeries || width === 0) {
      return undefined;
    }

    const svgRoot = chartRoot
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', svgHeight + margin.bottom + margin.top)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    svgRoot
      .append('g')
      .attr('transform', `translate(0,${svgHeight})`)
      .attr('class', 'x-axis');

    svgRoot.append('g').attr('class', 'y-axis');

    const subgroups = safeColumns.slice(1);
    const groups = safeData.map((entry) => entry.group);

    const x = d3.scaleBand().domain(groups).range([0, width]).padding(0.24);
    const yMax =
      d3.max(safeData, (entry) =>
        d3.max(
          Object.values(entry).filter(
            (value) => typeof value === 'number' && !Number.isNaN(value)
          )
        )
      ) || 0;
    const y = d3
      .scaleLinear()
      .domain([0, yMax || 1])
      .nice()
      .range([svgHeight, 0]);

    const xSubgroup = d3
      .scaleBand()
      .domain(subgroups)
      .range([0, Math.max(x.bandwidth(), subgroups.length * 16)])
      .padding(0.24);

    const seriesColorScale = d3
      .scaleOrdinal()
      .domain(subgroups)
      .range(seriesColors);

    const legendContainer = chartRoot
      .append('div')
      .attr('class', 'legend-container')
      .style('display', 'flex')
      .style('flex-direction', 'row')
      .style('gap', '20px')
      .style('margin-top', '5px')
      .style('justify-content', 'center')
      .style('flex-wrap', 'wrap');

    legendColorScale.domain().forEach((key) => {
      const legendItem = legendContainer
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('margin-bottom', '5px');

      legendItem
        .append('div')
        .style('width', '10px')
        .style('height', '10px')
        .style('background-color', legendColorScale(key))
        .style('margin-right', '5px');

      legendItem
        .append('span')
        .text(key)
        .style('font-size', '12px')
        .style('color', '#fff');
    });

    function mouseover(event, datum) {
      svgRoot.selectAll('.bars rect').attr('fill-opacity', 0.6);
      event.currentTarget?.setAttribute('fill-opacity', 1);

      const groupData =
        safeData.find((item) => item.group === datum.group) || {};
      const totalSentiment = subgroups.reduce(
        (sum, key) => sum + (groupData[key] || 0),
        0
      );
      const percentage = totalSentiment
        ? ((datum.value / totalSentiment) * 100).toFixed(2)
        : '0.00';

      setTooltipTitle(
        typeof datum.key === 'string'
          ? `${datum.key.charAt(0).toUpperCase()}${datum.key.slice(1)}`
          : datum.key
      );
      setTooltipValue(`${percentage}%`);
      setTooltipVisible(true);
    }

    function mousemove(event) {
      setTooltipTop(`${event.offsetY + margin.top}px`);
      setTooltipLeft(`${event.offsetX}px`);
    }

    function mouseleave() {
      svgRoot.selectAll('.bars rect').attr('fill-opacity', 1);
      setTooltipVisible(false);
    }

    svgRoot.selectAll('.x-axis').call(d3.axisBottom(x));

    if (xTickTotated) {
      svgRoot
        .selectAll('.x-axis text')
        .attr('transform', 'rotate(-40)')
        .attr('text-anchor', 'end')
        .attr('dx', '-.5em')
        .attr('dy', '.15em');
    }

    svgRoot.selectAll('.y-axis').call(d3.axisLeft(y).ticks(5));
    svgRoot.selectAll('.y-axis path, .y-axis line, .x-axis path, .x-axis line').remove();
    svgRoot.selectAll('.tick text').attr('class', 'chart_tick_text');

    svgRoot
      .selectAll('.bars')
      .data(safeData)
      .join('g')
      .attr('class', 'bars')
      .attr('transform', (entry) => `translate(${x(entry.group)}, 0)`)
      .selectAll('rect')
      .data((entry) =>
        subgroups.map((key) => ({
          key,
          value: entry[key] || 0,
          group: entry.group,
        }))
      )
      .join('rect')
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseout', mouseleave)
      .attr(
        'x',
        (datum) =>
          xSubgroup(datum.key) + (x.bandwidth() - xSubgroup.range()[1]) / 2
      )
      .attr('width', Math.max(12, xSubgroup.bandwidth()))
      .attr('rx', 6)
      .attr('y', (datum) => y(datum.value))
      .attr('height', (datum) => svgHeight - y(datum.value))
      .attr('fill', (datum) => seriesColorScale(datum.key));

    return () => {
      chartRoot.select('svg').remove();
      chartRoot.select('.legend-container').remove();
    };
  }, [
    hasSeries,
    id,
    legendDomainKey,
    margin.bottom,
    margin.left,
    margin.right,
    margin.top,
    safeColumns,
    safeData,
    seriesColorsKey,
    svgHeight,
    width,
    xTickTotated,
  ]);

  return (
    <div className="chart-wrapper">
      <div className="chart-container" id={`chart-${id}`} ref={chartRootRef}>
        {!hasSeries && (
          <p className="multy-bar-chart-empty">
            No chart data available for the selected filters.
          </p>
        )}
        <ChartTooltip
          top={tooltipTop}
          left={tooltipLeft}
          title={tooltipTitle}
          visible={tooltipVisible}
          value={tooltipValue}
        />
      </div>
      <div className="download-btns">
        <DownloadAsExcelButton data={safeData} columns={columns} fileName={fileName} />
        <DownloadAsPNGButton
          chartId={`chart-${id}`}
          fileName="bar-chart.png"
          metadata={{
            title,
            filters: formatFilters(filters),
            downloadDate: new Date().toLocaleDateString(),
            websiteName: 'JozaniAI for Tourism',
          }}
        />
      </div>
    </div>
  );
};

BarChart.propTypes = {};

BarChart.defaultProps = {};

export default BarChart;
